import { Box } from '@mui/material';
import debounce from 'lodash/debounce';
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DomSizeContext } from './helpers';

interface Props {
  children: ReactNode;
}

const DomSize: FC<Props> = ({ children }) => {
  const [size, setSize] = useState({ height: 0, width: 0 });
  const ref = useRef<HTMLDivElement>(null);

  const handleGetSize = useCallback(() => {
    const container = ref.current;

    if (!container) {
      return { height: 0, width: 0 };
    }

    return {
      height: container.clientHeight,
      width: container.clientWidth,
    };
  }, []);

  const debouncedHandleResize = useMemo(
    () =>
      debounce(() => {
        setSize(handleGetSize());
      }, 150),
    [handleGetSize]
  );

  useEffect(() => {
    setSize(handleGetSize());
  }, [handleGetSize]);

  useEffect(() => {
    window.addEventListener('resize', debouncedHandleResize);
    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
      debouncedHandleResize.cancel();
    };
  }, [debouncedHandleResize]);

  return (
    <DomSizeContext.Provider value={{ ...size, getSize: handleGetSize }}>
      <Box ref={ref} width="100%" height="100%" flexGrow={1}>
        {children}
      </Box>
    </DomSizeContext.Provider>
  );
};

export default DomSize;
